import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function LoginModal({ open, onClose }) {
    const [mode, setMode] = useState('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();

    if (!open) return null;

    const handleGoogleLogin = () => {
        window.location.href = `${API_URL}/api/auth/google/login`;
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const endpoint = mode === 'register' ? '/api/auth/register' : '/api/auth/login';
            const body = mode === 'register'
                ? { email, password, name }
                : { email, password };
            const res = await fetch(`${API_URL}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(body)
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Echec de l'authentification");
            login(data, data.session_token); // store token for cross-origin auth
            toast.success(mode === 'register' ? 'Compte cree !' : 'Bienvenue !');
            onClose();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="login-modal">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

            <div className="relative z-10 w-full max-w-[400px] bg-card border border-border rounded-xl shadow-xl p-8 mx-4" style={{ animation: 'fadeIn 200ms cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors" data-testid="login-modal-close">
                    <X className="w-4 h-4" />
                </button>

                <div className="text-center mb-7">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                        <span className="text-primary font-bold text-lg">K</span>
                    </div>
                    <h2 className="text-xl font-semibold tracking-tight">
                        {mode === 'login' ? 'Bon retour' : 'Creer votre compte'}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1.5">
                        {mode === 'login' ? 'Se connecter a Kinote' : 'Commencez a creer vos storyboards'}
                    </p>
                </div>

                <Button
                    variant="outline"
                    className="w-full h-10 text-sm font-medium mb-5 gap-2.5 hover:bg-secondary/50 transition-all duration-200"
                    onClick={handleGoogleLogin}
                    data-testid="google-login-btn"
                >
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Continuer avec Google
                </Button>

                <div className="relative mb-5">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
                    <div className="relative flex justify-center">
                        <span className="bg-card px-3 text-xs text-muted-foreground">ou</span>
                    </div>
                </div>

                <form onSubmit={handleEmailSubmit} className="space-y-3">
                    {mode === 'register' && (
                        <div>
                            <Input id="name" type="text" placeholder="Nom" value={name} onChange={(e) => setName(e.target.value)} className="h-10 text-sm ghost-input" required data-testid="register-name-input" />
                        </div>
                    )}
                    <div>
                        <Input id="email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-10 text-sm ghost-input" required data-testid="login-email-input" />
                    </div>
                    <div>
                        <Input id="password" type="password" placeholder="Mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} className="h-10 text-sm ghost-input" required minLength={6} data-testid="login-password-input" />
                    </div>
                    <Button type="submit" className="w-full h-10 font-medium text-sm" disabled={loading} data-testid="login-submit-btn">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === 'login' ? 'Se connecter' : 'Creer le compte')}
                    </Button>
                </form>

                <p className="text-center text-sm text-muted-foreground mt-5">
                    {mode === 'login' ? (
                        <>Pas de compte ? <button className="text-primary hover:underline font-medium" onClick={() => setMode('register')} data-testid="switch-to-register">S'inscrire</button></>
                    ) : (
                        <>Deja inscrit ? <button className="text-primary hover:underline font-medium" onClick={() => setMode('login')} data-testid="switch-to-login">Se connecter</button></>
                    )}
                </p>
            </div>
        </div>
    );
}
